import React, { useMemo } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';
import {
  calculateTicketsSoldFromRangeResult,
  resolveTicketPricePoint,
} from '../../features/audit/fixOcr/utils/ticketBalanceCalculations';

interface AuditDynamicFormTotalsProps {
  control: any;
  gamesData: Map<string, number>;
}

const AuditDynamicFormTotals: React.FC<AuditDynamicFormTotalsProps> = React.memo(({
  control,
  gamesData,
}) => {
  const watchedTicketDetails = useWatch({
    control,
    name: 'ticket_details',
  });

  const {
    ticketsSoldDataAccordingToPricePoint,
    totalPrice,
    totalTicketsSold,
  } = useMemo(() => {
    const ticketDetails = Array.isArray(watchedTicketDetails) ? watchedTicketDetails : [];
    const pricePointSoldCalculations = new Map<number, number>();

    ticketDetails.forEach((ticket: any) => {
      const pricePoint = resolveTicketPricePoint(ticket, gamesData);
      if (!pricePoint) {
        return;
      }

      const soldResult = calculateTicketsSoldFromRangeResult(ticket.range, ticket.ticketsPerPack);
      if (!soldResult.isValid) {
        return;
      }

      pricePointSoldCalculations.set(
        pricePoint,
        (pricePointSoldCalculations.get(pricePoint) ?? 0) + soldResult.sold,
      );
    });

    const sortedTicketsSoldDataAccordingToPricePoint = Array.from(pricePointSoldCalculations.entries()).sort(
      (prevValue, nextValue) => prevValue[0] - nextValue[0],
    );

    const totals = sortedTicketsSoldDataAccordingToPricePoint.reduce(
      (accumulator, [pricePoint, sold]) => {
        accumulator.totalTicketsSold += sold;
        accumulator.totalPrice += pricePoint * sold;
        return accumulator;
      },
      {
        totalTicketsSold: 0,
        totalPrice: 0,
      },
    );

    return {
      ticketsSoldDataAccordingToPricePoint: sortedTicketsSoldDataAccordingToPricePoint,
      totalPrice: totals.totalPrice,
      totalTicketsSold: totals.totalTicketsSold,
    };
  }, [gamesData, watchedTicketDetails]);

  return (
    <Box padding={2.5}>
      <Box>
        <Divider sx={{ borderColor: '#A3AED099', borderWidth: '0.5px' }} />
        <Box display="flex" sx={{ py: 0.5, minHeight: '23px', maxHeight: '23px', alignItems: 'center' }}>
          <Box width={'30%'}>
            <Typography variant="body2" sx={{ color: '#2C6CA0', fontSize: '15px', fontWeight: 600, textTransform: 'uppercase', textAlign: 'right', lineHeight: 1 }}>Price Point</Typography>
          </Box>
          <Box width={'30%'}>
            <Typography variant="body2" sx={{ color: '#2C6CA0', fontSize: '15px', fontWeight: 600, textTransform: 'uppercase', textAlign: 'right', lineHeight: 1 }}>Value</Typography>
          </Box>
          <Box width={'30%'}>
            <Typography variant="body2" sx={{ color: '#2C6CA0', fontSize: '15px', fontWeight: 600, textTransform: 'uppercase', textAlign: 'right', lineHeight: 1 }}>Sold</Typography>
          </Box>
        </Box>
        <Divider sx={{ borderColor: '#A3AED099', borderWidth: '0.5px' }} />

        {ticketsSoldDataAccordingToPricePoint.map((entry, index: number) => {
          const [pricePoint, sold] = entry as [number, number];
          const value = pricePoint * sold;
          return (
            <Box key={`price-point-row-${pricePoint}-${index}`}>
              <Box display="flex" sx={{ py: 0.5, minHeight: '23px', maxHeight: '23px', alignItems: 'center' }}>
                <Box width={'30%'}>
                  <Typography variant="body2" sx={{ color: '#222B45', fontSize: '15px', fontWeight: 500, textAlign: 'right', lineHeight: 1 }}>${pricePoint}</Typography>
                </Box>
                <Box width={'30%'}>
                  <Typography variant="body2" sx={{ color: '#222B45', fontSize: '15px', fontWeight: 500, textAlign: 'right', lineHeight: 1 }}>${value.toFixed(2)}</Typography>
                </Box>
                <Box width={'30%'}>
                  <Typography variant="body2" sx={{ color: '#222B45', fontSize: '15px', fontWeight: 500, textAlign: 'right', lineHeight: 1 }}>{sold}</Typography>
                </Box>
              </Box>
              <Divider sx={{ borderColor: '#A3AED099', borderWidth: '0.5px' }} />
            </Box>
          );
        })}
      </Box>

      <Box display="flex" sx={{ py: 1, mt: 2 }}>
        <Box width={'30%'}>
          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 17, textAlign: 'right' }}>Shift Total</Typography>
        </Box>
        <Box width={'30%'}>
          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 17, textAlign: 'right' }}>${totalPrice.toFixed(2)}</Typography>
        </Box>
        <Box width={'30%'}>
          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 17, textAlign: 'right' }}>{totalTicketsSold}</Typography>
        </Box>
      </Box>
    </Box>
  );
});

AuditDynamicFormTotals.displayName = 'AuditDynamicFormTotals';

export default AuditDynamicFormTotals;
